import { FC, ReactEventHandler, useEffect, useState, useMemo } from 'react';
import cn from 'classnames';
import {
  generateCatalogPageLink,
  generateFirstLevelCategoryPageLink,
  generateSecondLevelCategoryPageLink,
  generateThirdLevelCategoryPageLink
} from 'src/utils/links';
import { FirstCategoryLevel } from 'src/api/types';
import useMedia from 'src/hooks/useMedia';
import { FormattedMessage } from 'react-intl';
import useSSRMultyLevelCategoriesStore from 'src/hooks/useSSRMultyLevelCategoriesStore';
import NavigationList from './NavigationList';
import NavigationLink from '../NavigationLink/NavigationLink';
import trns from './trns';
import NextImage from '../NextImage/NextImage';

interface Props {
  onMouseOver?: ReactEventHandler<HTMLDivElement>;
  onCloseCatalog?: () => void;
  initialCategory?: FirstCategoryLevel;
  multyLevelCategoriesList?: FirstCategoryLevel[];
  selectedSubCategoryId?: string;
}
const allCategoriesIcon = 'shared/all-categories.svg';

const CategoriesDropdown: FC<Props> = ({
  onMouseOver,
  onCloseCatalog,
  initialCategory = null,
  selectedSubCategoryId
}) => {
  const { multyLevelCategoriesList } = useSSRMultyLevelCategoriesStore();
  const { xxs, m } = useMedia();
  const isMobileDevice = !m;
  const [selectedCategory, setSelectedCategory] =
    useState<FirstCategoryLevel | null>(
      multyLevelCategoriesList ? multyLevelCategoriesList[0] : null
    );

  useEffect(() => {
    // Handle force category set
    if (initialCategory && initialCategory.translitName) {
      setSelectedCategory(initialCategory);
    } else {
      setSelectedCategory(
        multyLevelCategoriesList ? multyLevelCategoriesList[0] : null
      );
    }
  }, [
    // TODO: array as useEffect dependency should be replaced with primitive. Or deep use effect utilized
    multyLevelCategoriesList,
    initialCategory
  ]);

  useEffect(() => {
    if (selectedSubCategoryId) {
      onCloseCatalog();
    }
  }, [selectedSubCategoryId]);

  const subCategoriesList = useMemo(
    () => (
      <div
        className={cn(
          'min-h-[475px] grid grid-cols-3 gap-y-8 gap-x-6 z-10 auto-rows-max auto-cols-min h-full xs:rounded-br-0',
          'xs:w-1/2 sm:w-auto xs:rounded-b-0'
        )}
      >
        {multyLevelCategoriesList?.map((category) => {
          // Render all items to make them visible for indexing
          // Hide all items except selected via CSS to show only selected item to a member
          return category?.subCategories
            ?.slice(0, 6)
            .map((categorySecondLevel, index) => {
              const isVisible =
                selectedCategory?.translitName === category.translitName;
              return (
                <div
                  key={`wrapper_${category.name}_${categorySecondLevel.name}_${index}`}
                  className={cn(
                    !isVisible && 'hidden',
                    'min-h-[36px]  border-white cursor-pointer text-normal px-5 border-b text-black'
                  )}
                >
                  <div className="w-full hover:bg-gallery hover:text-bahamaBlue p-2">
                    <NavigationLink
                      key={`link_${category.name}_${categorySecondLevel.name}`}
                      onClick={() => onCloseCatalog()}
                      href={generateSecondLevelCategoryPageLink({
                        subCategoryId: categorySecondLevel?.translitName,
                        category
                      })}
                      passClassName="fs-body-bold"
                    >
                      {categorySecondLevel.name}
                    </NavigationLink>
                  </div>
                  {categorySecondLevel?.subCategories?.length && (
                    <div className="w-full h-[1px] bg-alto my-2 px-2"></div>
                  )}
                  <div className="mb-3">
                    {categorySecondLevel?.subCategories
                      ?.slice(0, 3)
                      .map((categoryThirdLevel, thirdLevelIndex) => (
                        <div
                          key={`wrapper_inner_${category.name}_${categorySecondLevel.name}_${index}_${categoryThirdLevel.translitName}_${thirdLevelIndex}`}
                          className="flex flex-col hover:bg-gallery hover:text-bahamaBlue p-2 mx-[-8px] my-2"
                        >
                          <NavigationLink
                            key={`link_${category.name}_${categorySecondLevel.name}_${categoryThirdLevel.translitName}`}
                            onClick={() => onCloseCatalog()}
                            href={generateThirdLevelCategoryPageLink({
                              category,
                              secondCategoryId:
                                categorySecondLevel?.translitName,
                              thirdCategoryId: categoryThirdLevel?.translitName
                            })}
                            passClassName="fs-body-lg-reg !leading-6 hover:underline hover:text-bahamaBlue text-black opacity-70"
                          >
                            {categoryThirdLevel.name}
                          </NavigationLink>
                        </div>
                      ))}
                  </div>
                  {categorySecondLevel?.subCategories?.length > 0 && (
                    <NavigationLink
                      onClick={() => onCloseCatalog()}
                      href={generateSecondLevelCategoryPageLink({
                        subCategoryId: categorySecondLevel?.translitName,
                        category
                      })}
                      passClassName="fs-body-lg-reg !leading-6 underline text-astronautBlue"
                    >
                      <FormattedMessage {...trns.viewAllCategories} />
                    </NavigationLink>
                  )}
                </div>
              );
            });
        })}
      </div>
    ),
    [selectedSubCategoryId, selectedCategory]
  );

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
    <div
      // compendium-translate-z-0 fixes drop-shadow behavior for Safari
      className={cn(
        // compendium-translate-z-0 fixes drop-shadow behavior for Safari
        'compendium-constraints w-full fs-caption-reg relative h-full rounded-b-xl min-h-[475px] drop-shadow-md compendium-translate-z-0',
        {
          flex: xxs
        }
      )}
      onMouseOver={!isMobileDevice ? onMouseOver : null}
      onClick={isMobileDevice ? onMouseOver : null}
    >
      <div
        className={cn(
          'flex flex-col bg-opacity-100 z-20 bg-gallery rounded-bl-xl  min-h-[475px]'
        )}
      >
        <NavigationList
          listClassName={cn(
            'w-full min-w-[299px] pt-5 ',
            'xs:bg-gallery xs:w-1/2 sm:w-4/12 lm:w-3/12'
          )}
          items={multyLevelCategoriesList}
          selectedItem={selectedCategory}
          setSelectedItem={
            setSelectedCategory as (item: FirstCategoryLevel) => void
          }
        >
          {!xxs && subCategoriesList}
        </NavigationList>
        <NavigationLink
          key={selectedCategory?.translitName}
          href={generateCatalogPageLink()}
          passClassName="w-full flex items-center justify-start py-4 text-doveGray fs-body-bold hover:bg-white hover:text-black"
        >
          <div className="flex items-center justify-center w-19 ">
            <NextImage
              alt="allCategoriesIcon"
              src={allCategoriesIcon}
              layout="fixed"
              height={24}
              width={24}
            />
          </div>
          <FormattedMessage {...trns.viewCatalog} />
        </NavigationLink>
      </div>
      <div className="flex flex-col pt-[26px] pb-5 px-2.5 bg-white rounded-br-xl">
        {xxs && subCategoriesList}
        {selectedCategory?.subCategories?.length > 6 && (
          <div className="mt-8 ml-2">
            <NavigationLink
              key={selectedCategory?.translitName}
              onClick={() => onCloseCatalog()}
              href={generateFirstLevelCategoryPageLink({
                category: selectedCategory
              })}
              passClassName="fs-button-bold text-white w-auto rounded-md px-8 py-2 bg-astronautBlue"
            >
              <FormattedMessage
                {...trns.viewCategory}
                values={{
                  selectedCategory: selectedCategory?.name
                }}
              />
            </NavigationLink>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoriesDropdown;
